import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/** ─── DEMO FLAG: set to false to use live API data ─── */
const USE_MOCK_DATA = false;

/**
 * Monthly 8-Contact ANC Model — intervals from booking date.
 * Contact 1 = booking date (week 0), contacts 2-8 each +4 weeks.
 */
const CONTACT_SCHEDULE = [
    { contact: 1, weeks: 0,  label: "Contact 1 — Booking visit", trimester: 1 },
    { contact: 2, weeks: 4,  label: "Contact 2 — 4 weeks",       trimester: 1 },
    { contact: 3, weeks: 8,  label: "Contact 3 — 8 weeks",       trimester: 1 },
    { contact: 4, weeks: 12, label: "Contact 4 — 12 weeks",      trimester: 1 },
    { contact: 5, weeks: 16, label: "Contact 5 — 16 weeks",      trimester: 2 },
    { contact: 6, weeks: 20, label: "Contact 6 — 20 weeks",      trimester: 2 },
    { contact: 7, weeks: 24, label: "Contact 7 — 24 weeks",      trimester: 2 },
    { contact: 8, weeks: 28, label: "Contact 8 — 28 weeks",      trimester: 3 },
];

/**
 * Calculate a date by adding weeks to the LNMP date
 */
function addWeeksToDate(dateStr, weeks) {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + weeks * 7);
    return date.toISOString().slice(0, 10);
}

/**
 * Derive trimester (1/2/3) from gestational age in weeks.
 * Falls back to contact_number lookup in CONTACT_SCHEDULE.
 */
function deriveTrimester(gaWeeks, contactNumber) {
    if (gaWeeks !== null && gaWeeks !== undefined) {
        if (gaWeeks < 14) return 1;
        if (gaWeeks < 28) return 2;
        return 3;
    }
    const def = CONTACT_SCHEDULE.find(function(c) { return c.contact === contactNumber; });
    return def ? def.trimester : null;
}

/**
 * Determine the status of a scheduled contact
 */
function deriveStatus(schedule, today) {
    if (schedule.actual_date) return "attended";
    const scheduledDate = new Date(schedule.scheduled_date);
    const todayDate = new Date(today);
    if (scheduledDate < todayDate) return "missed";
    const daysUntil = Math.floor((scheduledDate - todayDate) / (24 * 60 * 60 * 1000));
    if (daysUntil <= 7) return "upcoming";
    return "future";
}

/**
 * useANCSchedule — Custom hook for ANC Visit Scheduling
 *
 * Manages:
 *  - Fetching booking data and schedule
 *  - Auto-generating schedule from LNMP
 *  - Status tracking (attended, upcoming, missed, future)
 *  - Reschedule dialog state
 *  - Progress summary statistics
 */
export const useANCSchedule = function(actions, patient_number, visit_id) {
    const today = new Date().toISOString().slice(0, 10);

    const _bookingData = useState(null);
    const bookingData = _bookingData[0];
    const setBookingData = _bookingData[1];

    const _loadingBooking = useState(true);
    const loadingBooking = _loadingBooking[0];
    const setLoadingBooking = _loadingBooking[1];

    const _schedule = useState([]);
    const schedule = _schedule[0];
    const setSchedule = _schedule[1];

    const _loadingSchedule = useState(false);
    const loadingSchedule = _loadingSchedule[0];
    const setLoadingSchedule = _loadingSchedule[1];

    const _rescheduleDialog = useState({ open: false, item: null });
    const rescheduleDialog = _rescheduleDialog[0];
    const setRescheduleDialog = _rescheduleDialog[1];

    const _rescheduleDate = useState("");
    const rescheduleDate = _rescheduleDate[0];
    const setRescheduleDate = _rescheduleDate[1];

    const _saving = useState(false);
    const saving = _saving[0];
    const setSaving = _saving[1];

    /* ─── Fetch booking data ─── */
    useEffect(function() {
        if (!patient_number) return;
        setLoadingBooking(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/booking/patient/" + patient_number)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) {
                    setBookingData(data);
                }
            })
            .catch(function() {
                setBookingData(null);
            })
            .finally(function() { setLoadingBooking(false); });
    }, [patient_number]);

    /* ─── Fetch schedule ─── */
    useEffect(function() {
        if (!bookingData || !bookingData.id) return;
        setLoadingSchedule(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/schedule/" + bookingData.id)
            .then(function(res) {
                const data = res.data || [];
                if (Array.isArray(data) && data.length > 0) {
                    // Add derived status and trimester to each schedule item
                    const enriched = data.map(function(item) {
                        return Object.assign({}, item, {
                            derived_status: item.status || deriveStatus(item, today),
                            trimester: item.trimester != null
                                ? item.trimester
                                : deriveTrimester(item.scheduled_gestational_age_weeks, item.contact_number),
                        });
                    });
                    setSchedule(enriched);
                } else {
                    // Generate schedule client-side from LNMP
                    generateLocalSchedule();
                }
            })
            .catch(function() {
                // Fallback: generate client-side
                generateLocalSchedule();
            })
            .finally(function() { setLoadingSchedule(false); });
    }, [bookingData]);

    /* ─── Generate schedule from booking date (client-side fallback) ─── */
    function generateLocalSchedule() {
        if (!bookingData || !bookingData.booking_date) return;
        const bookingDate = bookingData.booking_date;
        const lnmp = bookingData.lnmp;
        const generated = CONTACT_SCHEDULE.map(function(contact) {
            const scheduledDate = addWeeksToDate(bookingDate, contact.weeks);
            const gaWeeks = lnmp
                ? Math.max(0, Math.floor((new Date(scheduledDate) - new Date(lnmp)) / (7 * 24 * 60 * 60 * 1000)))
                : contact.weeks;
            return {
                id: null,
                booking_id: bookingData.id,
                contact_number: contact.contact,
                scheduled_date: scheduledDate,
                scheduled_gestational_age_weeks: gaWeeks,
                actual_date: null,
                contact_id: null,
                status: null,
                derived_status: deriveStatus({ scheduled_date: scheduledDate, actual_date: null }, today),
                label: contact.label,
                trimester: contact.trimester,
            };
        });
        setSchedule(generated);
    }

    /* ─── Current gestational age ─── */
    const gestationalAge = useMemo(function() {
        if (!bookingData || !bookingData.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const todayDate = new Date();
        const diffMs = todayDate - lnmpDate;
        if (diffMs < 0) return null;
        const totalDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
        const weeks = Math.floor(totalDays / 7);
        const days = totalDays % 7;
        return { weeks: weeks, days: days, display: weeks + "+" + days };
    }, [bookingData]);

    /* ─── Progress summary ─── */
    const summary = useMemo(function() {
        let attended = 0;
        let missed = 0;
        let upcoming = 0;
        let future = 0;
        schedule.forEach(function(item) {
            const s = item.derived_status || "future";
            if (s === "attended") attended++;
            else if (s === "missed") missed++;
            else if (s === "upcoming") upcoming++;
            else future++;
        });
        const total = schedule.length || 8;
        const progressPercent = Math.round((attended / total) * 100);
        return {
            attended: attended,
            missed: missed,
            upcoming: upcoming,
            future: future,
            total: total,
            progressPercent: progressPercent,
        };
    }, [schedule]);

    /* ─── Next contact ─── */
    const nextContact = useMemo(function() {
        const upcomingItems = schedule.filter(function(item) {
            return item.derived_status === "upcoming" || item.derived_status === "future";
        });
        if (upcomingItems.length === 0) return null;
        return upcomingItems[0];
    }, [schedule]);

    /* ─── Open reschedule dialog ─── */
    const openReschedule = useCallback(function(item) {
        setRescheduleDialog({ open: true, item: item });
        setRescheduleDate(item.scheduled_date || "");
    }, []);

    const closeReschedule = useCallback(function() {
        setRescheduleDialog({ open: false, item: null });
        setRescheduleDate("");
    }, []);

    /* ─── Submit reschedule ─── */
    const handleReschedule = useCallback(function() {
        if (!rescheduleDate || !rescheduleDialog.item) return;
        setSaving(true);

        const item = rescheduleDialog.item;

        if (item.id) {
            // Update existing schedule item via API
            axios
                .put(config.smsUrl + "/obgyn/anc/schedule/" + item.id, {
                    scheduled_date: rescheduleDate,
                    status: "rescheduled",
                })
                .then(function() {
                    if (actions && actions.snackbarActions) {
                        actions.snackbarActions.snackSuccess("Contact " + item.contact_number + " rescheduled successfully");
                    }
                    // Update local state
                    setSchedule(function(prev) {
                        return prev.map(function(s) {
                            if (s.contact_number === item.contact_number) {
                                return Object.assign({}, s, {
                                    scheduled_date: rescheduleDate,
                                    status: "rescheduled",
                                    derived_status: deriveStatus({ scheduled_date: rescheduleDate, actual_date: null }, today),
                                });
                            }
                            return s;
                        });
                    });
                    closeReschedule();
                })
                .catch(function() {
                    if (actions && actions.snackbarActions) {
                        actions.snackbarActions.snackError("Failed to reschedule contact");
                    }
                })
                .finally(function() { setSaving(false); });
        } else {
            // Local-only update (no server record yet)
            setSchedule(function(prev) {
                return prev.map(function(s) {
                    if (s.contact_number === item.contact_number) {
                        return Object.assign({}, s, {
                            scheduled_date: rescheduleDate,
                            status: "rescheduled",
                            derived_status: deriveStatus({ scheduled_date: rescheduleDate, actual_date: null }, today),
                        });
                    }
                    return s;
                });
            });
            if (actions && actions.snackbarActions) {
                actions.snackbarActions.snackSuccess("Contact " + item.contact_number + " rescheduled");
            }
            closeReschedule();
            setSaving(false);
        }
    }, [rescheduleDate, rescheduleDialog, actions, today]);

    /* ─── Re-fetch schedule from server ─── */
    const generateSchedule = useCallback(function() {
        if (!bookingData || !bookingData.id) return;
        setSaving(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/schedule/" + bookingData.id)
            .then(function(res) {
                const data = res.data || [];
                if (Array.isArray(data) && data.length > 0) {
                    const enriched = data.map(function(item) {
                        return Object.assign({}, item, {
                            derived_status: item.status || deriveStatus(item, today),
                        });
                    });
                    setSchedule(enriched);
                } else {
                    generateLocalSchedule();
                }
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackSuccess("Schedule refreshed");
                }
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackError("Failed to refresh schedule");
                }
            })
            .finally(function() { setSaving(false); });
    }, [bookingData, actions, today]);

    return {
        bookingData: bookingData,
        loadingBooking: loadingBooking,
        schedule: schedule,
        loadingSchedule: loadingSchedule,
        gestationalAge: gestationalAge,
        summary: summary,
        nextContact: nextContact,
        rescheduleDialog: rescheduleDialog,
        rescheduleDate: rescheduleDate,
        setRescheduleDate: setRescheduleDate,
        openReschedule: openReschedule,
        closeReschedule: closeReschedule,
        handleReschedule: handleReschedule,
        generateSchedule: generateSchedule,
        saving: saving,
        CONTACT_SCHEDULE: CONTACT_SCHEDULE,
    };
};
